/**
 * Logged-in landing content: hero + the founder's projects with an optional
 * "New project" card. When the New project card is clicked, the hero shows
 * IdeaChatInput and the card hides.
 *
 * Two layouts, chosen by `lifecycleFlags` (read server-side in
 * `src/app/page.tsx`, never from the client flag hook: SSR has no flag values,
 * so a client read would flash the flat grid and then pop sections in):
 * - `archive` off: today's flat `LandingProjects` grid, delete only.
 * - `archive` on: `LandingProjectSections` (Active / Shared / Archived /
 *   Deleted); the Deleted group and restore are further gated by `hardDelete`.
 *
 * Lifecycle moves are optimistic (`/optimistic-updates`, Pattern 2): the
 * override for a project id is written synchronously, the mutation runs, and
 * on success `router.refresh()` re-reads the server list. The override stays
 * until the server row agrees with it or the `projectLifecycleTracker` pin
 * expires: `projects.list` reads a replica, so the first refresh can still
 * return the old state, and dropping the override on success alone would
 * snap the card back. The pin is re-marked immediately before each
 * `router.refresh()`, not just at click time, because a mutation slower than
 * the pin's window would otherwise have already expired by the time the
 * refresh lands. A CONFLICT means someone (or another tab) already made the
 * move; it is not an error to the founder, so it refreshes instead of
 * toasting red.
 *
 * Delete is two mutations, chosen by `hardDeleteEnabled`: off uses
 * `projects.softDelete` (today's one-stage delete, its own toast via
 * `withToast`, and the client `trackProjectDeleted` event); on uses
 * `projects.deleteNow` through the SAME override/tracker/CONFLICT path as
 * archive/unarchive/restore, and never calls `trackProjectDeleted` — the
 * server emits `project_deleted` itself for that mutation.
 *
 * `purge` ("Delete now" on a Deleted card) is `projects.purgeNow` on the same
 * path again: its optimistic override is `purgeRequestedAt`, which is what the
 * card reads to show "Deleting now" instead of the countdown.
 *
 * `source` on an archive move records where it was chosen; only the delete
 * dialog's "Archive instead" sets it, every other archive call omits it.
 */
"use client";

import { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { api } from "@/lib/trpc/react";
import { trackProjectDeleted } from "@/lib/analytics/client";
import { captureClientError } from "@/lib/sentry/capture-client";
import { peekResumeChat } from "@/lib/onboarding/resumeChat";
import { withToast } from "@/lib/ui/useMutationWithToast";
import { projectLifecycleTracker } from "@/state/projectLifecycleTracker";
import type {
  ProjectLifecycleAction,
  ProjectLifecycleActionOptions,
} from "@/constants/projectLifecycle";
import LandingHero from "@/components/organisms/landing/LandingHero";
import LandingProjects, {
  type LandingProjectListItem,
} from "@/components/organisms/landing/LandingProjects";
import { LandingProjectSections } from "@/components/organisms/landing/LandingProjectSections";
import { groupLandingProjects } from "@/components/organisms/landing/landingProjectGrouping";
import { AcceleratorsAnnouncementBanner } from "@/components/molecules/landing/AcceleratorsAnnouncementBanner";
import { useAcceleratorsAnnouncementBanner } from "@/components/molecules/landing/useAcceleratorsAnnouncementBanner";

export interface ProjectLifecycleFlags {
  /** `project-archive`: sectioned layout + archive/unarchive. */
  archive: boolean;
  /** `project-hard-delete`: Deleted section + restore. Only meaningful when `archive` is on. */
  hardDelete: boolean;
}

/** The lifecycle fields a pending move has already changed on the client. */
type LifecycleOverride = Pick<
  Partial<LandingProjectListItem>,
  "archivedAt" | "deletedAt" | "purgeRequestedAt"
>;

export interface LoggedInLandingContentProps {
  firstName?: string;
  projects: Array<LandingProjectListItem>;
  /** Viewer's app user id — used to mark "You" vs other owners on cards. */
  viewerUserId?: string;
  /** True when the SSR projects.list call threw — triggers a client-side retry. */
  projectsFetchFailed?: boolean;
  userInitials?: string;
  /** Server-evaluated lifecycle flags; the default is today's flat grid. */
  lifecycleFlags?: ProjectLifecycleFlags;
}

const FLAGS_OFF: ProjectLifecycleFlags = { archive: false, hardDelete: false };

function trpcErrorCode(err: unknown): unknown {
  return (err as { data?: { code?: unknown } | null } | null)?.data?.code;
}

function isConflictError(err: unknown): boolean {
  return trpcErrorCode(err) === "CONFLICT";
}

/**
 * Founder-facing copy for a failed lifecycle move. Mapped from the structured
 * tRPC code only, never `err.message`: the server's wording is written for a
 * log line ("Only the project owner can change its lifecycle state."), not a
 * founder, and can describe internals the UI must not repeat.
 */
function friendlyErrorMessage(err: unknown): string {
  switch (trpcErrorCode(err)) {
    case "FORBIDDEN":
      return "Only the project owner can do that.";
    case "PRECONDITION_FAILED":
      return "This project is frozen. Unarchive or restore it first.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/** True when the server row already reflects every field the override set. */
function serverAgrees(row: LandingProjectListItem, override: LifecycleOverride): boolean {
  return (Object.keys(override) as Array<keyof LifecycleOverride>).every(
    (key) => (row[key] == null) === (override[key] == null),
  );
}

export default function LoggedInLandingContent({
  firstName,
  projects: ssrProjects,
  viewerUserId,
  projectsFetchFailed = false,
  userInitials,
  lifecycleFlags = FLAGS_OFF,
}: LoggedInLandingContentProps) {
  const { archive: archiveEnabled } = lifecycleFlags;
  // `hardDelete` never applies without `archive`: the Deleted section lives
  // inside the sectioned layout.
  const hardDeleteEnabled = archiveEnabled && lifecycleFlags.hardDelete;

  // Client-side fallback: only retry if SSR actually failed, not for genuinely
  // empty accounts. Same widening as the SSR call so the fallback shows the
  // same rows.
  const { data: clientProjects, isLoading: isFallbackLoading, isError: isFallbackError, error: fallbackError, refetch } = api.projects.list.useQuery(
    { includeArchived: archiveEnabled, includeDeleted: hardDeleteEnabled },
    {
      enabled: projectsFetchFailed,
      retry: 2,
    },
  );
  const projects = clientProjects ?? ssrProjects;
  const isLoadingFallback = projectsFetchFailed && isFallbackLoading;
  const isFetchError = projectsFetchFailed && isFallbackError;

  // Report client-side fallback failures to Sentry. No impact passed: this
  // keeps the previous call's default (unclassified) "error" level — a
  // founder still sees the retry button either way, so it isn't "blocking".
  useEffect(() => {
    if (isFetchError && fallbackError) {
      captureClientError(fallbackError, {
        feature: "landing-projects",
        errorType: "projects-list-fetch-failed",
        tags: { trpcProcedure: "projects.list" },
        contexts: { fallback: { trigger: "ssr-fetch-failed", retries: 2 } },
      });
    }
  }, [isFetchError, fallbackError]);

  const [showNewIdeaChat, setShowNewIdeaChat] = useState(false);
  // A founder who signed in mid-chat lands here with the resume marker set:
  // open the chat so IdeaChatInput can auto-restore it (LEV-1925). An effect,
  // not a lazy useState initialiser: sessionStorage does not exist on the
  // server, and reading it during render would mismatch hydration.
  useEffect(() => {
    if (peekResumeChat()) setShowNewIdeaChat(true);
  }, []);

  // Captured once per mount so every "N days ago" label on the page shares one
  // clock. The server and client values differ by the render gap (milliseconds
  // to seconds), which cannot move a day-granular label except at the exact
  // midnight boundary, so no hydration mismatch in practice.
  const [now] = useState(() => Date.now());

  /** Pending lifecycle moves, applied over the server rows; see file header. */
  const [lifecycleOverrides, setLifecycleOverrides] = useState<Map<string, LifecycleOverride>>(
    () => new Map(),
  );
  const setOverride = useCallback((projectId: string, override: LifecycleOverride) => {
    setLifecycleOverrides((prev) => new Map(prev).set(projectId, override));
  }, []);
  const dropOverride = useCallback((projectId: string) => {
    setLifecycleOverrides((prev) => {
      if (!prev.has(projectId)) return prev;
      const next = new Map(prev);
      next.delete(projectId);
      return next;
    });
  }, []);

  const router = useRouter();
  const softDeleteMutation = api.projects.softDelete.useMutation(
    withToast({
      successMessage: "Project deleted",
      onSuccess: (_data, variables) => {
        trackProjectDeleted({ projectId: variables.projectId });
        // Re-arm the pin immediately before refresh: a mutation slower than
        // the window would otherwise have already expired by the time
        // router.refresh() re-reads a replica that may still be lagging.
        projectLifecycleTracker.mark(variables.projectId, Date.now());
        // The override stays until the refreshed list drops the row (or shows
        // it deleted) so the card does not reappear in between.
        router.refresh();
      },
      onError: (_error, variables) => {
        dropOverride(variables.projectId);
        projectLifecycleTracker.clear(variables.projectId);
      },
    }),
  );
  const { mutateAsync: softDeleteAsync } = softDeleteMutation;
  const { mutateAsync: archiveAsync } = api.projects.archive.useMutation();
  const { mutateAsync: unarchiveAsync } = api.projects.unarchive.useMutation();
  const { mutateAsync: restoreAsync } = api.projects.restore.useMutation();
  const { mutateAsync: deleteNowAsync } = api.projects.deleteNow.useMutation();
  const { mutateAsync: purgeNowAsync } = api.projects.purgeNow.useMutation();

  const handleLifecycleAction = useCallback(
    async (
      action: ProjectLifecycleAction,
      projectId: string,
      opts?: ProjectLifecycleActionOptions,
    ) => {
      const override: LifecycleOverride =
        action === "archive"
          ? { archivedAt: new Date() }
          : action === "unarchive"
            ? { archivedAt: null }
            : action === "restore"
              ? { deletedAt: null }
              : action === "purge"
                ? { purgeRequestedAt: new Date() }
                : { deletedAt: new Date() };
      setOverride(projectId, override);
      projectLifecycleTracker.mark(projectId, Date.now());

      if (action === "delete" && !hardDeleteEnabled) {
        // `withToast` owns delete's success/error toasts and the revert; its
        // own rejection is still caught here, or a failed delete becomes an
        // unhandled promise rejection even though the toast already ran.
        await softDeleteAsync({ projectId }).catch(() => {
          /* expected: withToast's onError already reverted the override and toasted */
        });
        return;
      }

      // Two-stage delete (`deleteNow`) and `purgeNow` share
      // archive/unarchive/restore's override/tracker/CONFLICT handling below:
      // same owner gating, and the server (not this client) emits the
      // `project_deleted` / purge events for those paths.
      try {
        if (action === "archive") {
          await archiveAsync({ projectId, ...(opts?.source ? { source: opts.source } : {}) });
        } else {
          const mutate =
            action === "unarchive"
              ? unarchiveAsync
              : action === "restore"
                ? restoreAsync
                : action === "purge"
                  ? purgeNowAsync
                  : deleteNowAsync;
          await mutate({ projectId });
        }
        // Re-arm the pin immediately before refresh: a mutation slower than
        // the window would otherwise have already expired by the time
        // router.refresh() re-reads a replica that may still be lagging.
        projectLifecycleTracker.mark(projectId, Date.now());
        router.refresh();
      } catch (err) {
        // expected: the server refused the move. CONFLICT means it already
        // happened (another tab, a double click), so the refreshed list is the
        // truth; anything else (FORBIDDEN when the flag flipped off mid-session,
        // a server fault the tRPC onError already captured) reverts the card.
        dropOverride(projectId);
        projectLifecycleTracker.clear(projectId);
        if (isConflictError(err)) {
          router.refresh();
          toast.info("Already updated. Refreshing.");
        } else {
          toast.error(friendlyErrorMessage(err));
        }
      }
    },
    [
      archiveAsync,
      deleteNowAsync,
      dropOverride,
      hardDeleteEnabled,
      purgeNowAsync,
      restoreAsync,
      router,
      setOverride,
      softDeleteAsync,
      unarchiveAsync,
    ],
  );
  const handleDeleteProject = useCallback(
    (projectId: string) => handleLifecycleAction("delete", projectId),
    [handleLifecycleAction],
  );

  // Reconcile after every server list change: an override is dropped once the
  // server row agrees with it, once the row is gone (deleted, or archived with
  // the flag off), or once its replica-lag pin has expired. Otherwise the
  // server row is still stale and the override keeps winning.
  useEffect(() => {
    setLifecycleOverrides((prev) => {
      if (prev.size === 0) return prev;
      const rowsById = new Map(projects.map((p) => [p.id, p]));
      const reconcileAt = Date.now();
      let changed = false;
      const next = new Map(prev);
      next.forEach((override, id) => {
        const row = rowsById.get(id);
        const settled = !row || serverAgrees(row, override);
        if (settled || !projectLifecycleTracker.isPending(id, reconcileAt)) {
          next.delete(id);
          projectLifecycleTracker.clear(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [projects]);

  const rowsWithOverrides = projects.map((p) => {
    const override = lifecycleOverrides.get(p.id);
    return override ? { ...p, ...override } : p;
  });

  // Must read `rowsWithOverrides`, not `projects`: the cards (and the
  // "Open Programs" href this computes) already show an archive/delete the
  // instant it is clicked, optimistically, before the server list agrees.
  // Reading the raw server rows here left the banner's link pointed at a
  // project that had just become inaccessible (resolveProjectAccess excludes
  // archived/deleted) until the next projects.list refetch caught up.
  const announcement = useAcceleratorsAnnouncementBanner({
    projects: rowsWithOverrides,
    viewerUserId,
  });

  return (
    <>
      <LandingHero
        firstName={firstName}
        showIdeaInputWhenSignedIn={showNewIdeaChat}
      />
      {/*
        LEV-2797: a reusable slot directly under the hero's "Let's get
        cracking." subtitle and above the projects section. LEV-2752 will
        mount other announcements here too. Was above the hero (page top);
        only its position moved, never its copy, flag gating or dismissal.
      */}
      {announcement.visible && (
        <div className="w-full max-w-[1032px] mx-auto px-4 pt-8 pb-2 md:pt-10">
          <AcceleratorsAnnouncementBanner
            href={announcement.href}
            onButtonClick={announcement.onButtonClick}
            onDismiss={announcement.onDismiss}
          />
        </div>
      )}
      {isLoadingFallback ? (
        <section className="bg-landing-bg-light py-16 px-4" aria-busy>
          <div className="w-full max-w-[1032px] mx-auto">
            <p className="sr-only" role="status" aria-live="polite">Loading projects</p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-hidden>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-48 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          </div>
        </section>
      ) : isFetchError ? (
        <section className="bg-landing-bg-light py-16 px-4">
          <div className="w-full max-w-[1032px] mx-auto text-center">
            <p className="text-gray-600 mb-4">
              We couldn&apos;t load your projects. This is usually temporary.
            </p>
            <button
              onClick={() => refetch()}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
            >
              Try again
            </button>
          </div>
        </section>
      ) : archiveEnabled ? (
        <LandingProjectSections
          groups={groupLandingProjects(rowsWithOverrides, viewerUserId, {
            showDeleted: hardDeleteEnabled,
          })}
          viewerUserId={viewerUserId}
          userInitials={userInitials}
          showNewProjectCard={!showNewIdeaChat}
          onNewProjectClick={() => setShowNewIdeaChat(true)}
          canRestore={hardDeleteEnabled}
          onLifecycleAction={handleLifecycleAction}
          now={now}
        />
      ) : (
        <LandingProjects
          // Flag off: a pending delete hides the row, exactly as before sections.
          projects={rowsWithOverrides.filter((p) => !p.deletedAt)}
          viewerUserId={viewerUserId}
          userInitials={userInitials}
          showNewProjectCard={!showNewIdeaChat}
          onNewProjectClick={() => setShowNewIdeaChat(true)}
          onDeleteProject={handleDeleteProject}
        />
      )}
    </>
  );
}
